import { useMemo } from 'react';
import { createWithEqualityFn as create } from 'zustand/traditional';
import type { DiscountDto, MenuSectionDto, OSDiscountDto } from '../../generated-api';
import { useMenuSectionsStore } from './MenuSectionsStore';
import { getAllItemsFromSections } from '../utils/cart';

/**
 * Map ordr-service's discount to more minimal DTO
 * Filters inactive/deleted and extracts discount code strings
 */
function mapToDiscountV3(raw: OSDiscountDto): DiscountDto {
  // SectionIds comes as Set<string> from backend, convert to array
  const sectionIds = raw.sectionIds ? [...raw.sectionIds] : [];

  return {
    amount: raw.amount,
    discountCodes: (raw.discountCodes ?? []).filter((dc) => dc.active && !dc.deleted).map((dc) => dc.discountCode),
    id: raw.discountId,
    isAllSectionsSelected: raw.isAllSectionsSelected,
    name: raw.name,
    productIds: [], // Frontend computes from sections
    sectionIds,
    type: raw.type,
  };
}

/**
 * Map and filter raw discounts (V3 mapping - mirrors backend mapForV3)
 */
function mapDiscountsForV3(rawDiscounts: OSDiscountDto[]): DiscountDto[] {
  return rawDiscounts.filter((d) => d.active && !d.deleted).map(mapToDiscountV3);
}

interface DiscountActions {
  setDiscounts: (discounts: OSDiscountDto[], locationId: string) => void;
  clear: () => void;
}

export interface DiscountState {
  discounts: DiscountDto[];
  locationId: string | undefined;
  actions: DiscountActions;
}

export const useDiscountStore = create<DiscountState>()((set) => ({
  actions: {
    clear: () => set({ discounts: [], locationId: undefined }),
    setDiscounts: (osDiscounts, locationId) => set({ discounts: mapDiscountsForV3(osDiscounts), locationId }),
  },
  discounts: [],
  locationId: undefined,
}));

export const useDiscountActions = () => useDiscountStore((state) => state.actions);

/**
 * Compute productIds for a discount based on menu sections.
 * Mirrors the backend DiscountMapper logic that was previously done server-side.
 *
 * @see backend/.../mappers/DiscountMapper.kt
 */
function computeProductIds(discount: DiscountDto, sections: MenuSectionDto[]): string[] {
  // Compute from sections (V3+ behavior)
  const eligibleSections = discount.isAllSectionsSelected
    ? sections
    : sections.filter((s) => discount.sectionIds.includes(s.sectionId));

  const eligibleItems = getAllItemsFromSections(eligibleSections);

  const itemIds = eligibleItems.map((item) => item.variantLinkId);

  const optionGroupIds = eligibleItems
    .flatMap((item) => item.optionGroups)
    .filter((og) => og.variantLinkId)
    .map((og) => og.variantLinkId!);

  // Use Set to deduplicate
  return [...new Set([...itemIds, ...optionGroupIds])];
}

/**
 * Returns discounts with computed productIds based on current menu sections.
 * Reactively updates when either discounts or sections change.
 */
export const useDiscounts = (): DiscountDto[] => {
  const discounts = useDiscountStore((state) => state.discounts);
  const sections = useMenuSectionsStore((state) => state.sections);

  return useMemo(
    () =>
      discounts.map((discount) => ({
        ...discount,
        productIds: computeProductIds(discount, sections),
      })),
    [discounts, sections]
  );
};
